import {
  Timestamp,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Group, GroupMember } from '../types/tournament';

export class GroupService {
  private readonly GROUPS_COLLECTION = 'groups';
  private readonly USERS_COLLECTION = 'users';

  /**
   * Create a new persistent group
   */
  async createGroup(
    name: string,
    createdBy: string,
    creatorDisplayName: string,
    creatorPhotoURL: string | undefined,
    memberIds: string[] = []
  ): Promise<string> {
    const groupRef = doc(collection(db, this.GROUPS_COLLECTION));
    const groupId = groupRef.id;

    // Get creator's full profile data for custom avatar
    const creatorDoc = await getDoc(doc(db, this.USERS_COLLECTION, createdBy));
    const creatorData = creatorDoc.exists() ? creatorDoc.data() : {};

    // Get member info
    const members: GroupMember[] = [
      {
        userId: createdBy,
        displayName: creatorDisplayName,
        photoURL: creatorPhotoURL || null,
        customAvatarType: creatorData.customAvatarType || null,
        customAvatarValue: creatorData.customAvatarValue || null,
        avatarBackgroundType: creatorData.avatarBackgroundType || null,
        avatarBackgroundValue: creatorData.avatarBackgroundValue || null,
        role: 'owner',
        joinedAt: new Date(),
      },
    ];

    // Add other members
    for (const memberId of memberIds) {
      if (memberId !== createdBy) {
        const userDoc = await getDoc(doc(db, this.USERS_COLLECTION, memberId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          members.push({
            userId: memberId,
            displayName: userData.displayName || 'Unknown',
            photoURL: userData.photoURL || null,
            customAvatarType: userData.customAvatarType || null,
            customAvatarValue: userData.customAvatarValue || null,
            avatarBackgroundType: userData.avatarBackgroundType || null,
            avatarBackgroundValue: userData.avatarBackgroundValue || null,
            role: 'member',
            joinedAt: new Date(),
          });
        }
      }
    }

    await setDoc(groupRef, {
      id: groupId,
      name,
      createdBy,
      createdAt: Timestamp.now(),
      members: members.map((m) => ({
        ...m,
        joinedAt: Timestamp.fromDate(m.joinedAt),
      })),
      isPersistent: true,
      settings: {
        maxMembers: 10,
      },
    });

    return groupId;
  }

  /**
   * Get all groups for a user
   * Fetches fresh avatar data for all members
   */
  async getGroups(userId: string): Promise<Group[]> {
    const groupsRef = collection(db, this.GROUPS_COLLECTION);
    
    // We need to get all groups and filter client-side due to Firestore limitations
    const allGroupsSnapshot = await getDocs(groupsRef);
    const groups: Group[] = [];

    for (const docSnapshot of allGroupsSnapshot.docs) {
      const data = docSnapshot.data();
      const members = data.members || [];
      
      // Check if user is a member
      if (members.some((m: GroupMember) => m.userId === userId)) {
        // Fetch fresh avatar data for each member
        const membersWithAvatars = await Promise.all(
          members.map(async (m: any) => {
            const userDoc = await getDoc(doc(db, this.USERS_COLLECTION, m.userId));
            const userData = userDoc.exists() ? userDoc.data() : {};
            
            return {
              userId: m.userId,
              displayName: m.displayName,
              photoURL: m.photoURL,
              customAvatarType: userData.customAvatarType,
              customAvatarValue: userData.customAvatarValue,
              avatarBackgroundType: userData.avatarBackgroundType,
              avatarBackgroundValue: userData.avatarBackgroundValue,
              role: m.role,
              joinedAt: m.joinedAt.toDate(),
            };
          })
        );

        groups.push({
          id: docSnapshot.id,
          name: data.name,
          createdBy: data.createdBy,
          createdAt: data.createdAt.toDate(),
          members: membersWithAvatars,
          isPersistent: true,
          settings: data.settings,
        });
      }
    }

    return groups;
  }

  /**
   * Get group by ID
   */
  async getGroup(groupId: string): Promise<Group | null> {
    const groupDoc = await getDoc(doc(db, this.GROUPS_COLLECTION, groupId));
    if (!groupDoc.exists()) {
      return null;
    }

    const data = groupDoc.data();
    return {
      id: groupDoc.id,
      name: data.name,
      createdBy: data.createdBy,
      createdAt: data.createdAt.toDate(),
      members: data.members.map((m: any) => ({
        userId: m.userId,
        displayName: m.displayName,
        photoURL: m.photoURL,
        role: m.role,
        joinedAt: m.joinedAt.toDate(),
      })),
      isPersistent: true,
      settings: data.settings,
    };
  }

  /**
   * Invite user to group
   */
  async inviteToGroup(groupId: string, userId: string): Promise<void> {
    const groupDoc = await getDoc(doc(db, this.GROUPS_COLLECTION, groupId));
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const group = groupDoc.data();
    
    // Check if already a member
    if (group.members.some((m: GroupMember) => m.userId === userId)) {
      throw new Error('User is already a member of this group');
    }

    // Check max members
    if (group.members.length >= group.settings.maxMembers) {
      throw new Error('Group is full');
    }

    // Get user info
    const userDoc = await getDoc(doc(db, this.USERS_COLLECTION, userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const newMember: GroupMember = {
      userId,
      displayName: userData.displayName || 'Unknown',
      photoURL: userData.photoURL || null,
      customAvatarType: userData.customAvatarType || null,
      customAvatarValue: userData.customAvatarValue || null,
      avatarBackgroundType: userData.avatarBackgroundType || null,
      avatarBackgroundValue: userData.avatarBackgroundValue || null,
      role: 'member',
      joinedAt: new Date(),
    };

    await updateDoc(doc(db, this.GROUPS_COLLECTION, groupId), {
      members: arrayUnion({
        ...newMember,
        joinedAt: Timestamp.fromDate(newMember.joinedAt),
      }),
    });
  }

  /**
   * Remove user from group
   */
  async removeFromGroup(groupId: string, userId: string, requesterId: string): Promise<void> {
    const groupDoc = await getDoc(doc(db, this.GROUPS_COLLECTION, groupId));
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const group = groupDoc.data();

    // Check permissions (owner can remove anyone, members can only remove themselves)
    if (requesterId !== group.createdBy && requesterId !== userId) {
      throw new Error('You do not have permission to remove this user');
    }

    // Cannot remove owner
    if (userId === group.createdBy) {
      throw new Error('Cannot remove group owner');
    }

    // Find and remove member
    const memberToRemove = group.members.find((m: any) => m.userId === userId);
    if (!memberToRemove) {
      throw new Error('User is not a member of this group');
    }

    await updateDoc(doc(db, this.GROUPS_COLLECTION, groupId), {
      members: arrayRemove(memberToRemove),
    });
  }

  /**
   * Leave group (for non-owners)
   */
  async leaveGroup(groupId: string, userId: string): Promise<void> {
    await this.removeFromGroup(groupId, userId, userId);
  }

  /**
   * Delete group (owner only)
   */
  async deleteGroup(groupId: string, userId: string): Promise<void> {
    const groupDoc = await getDoc(doc(db, this.GROUPS_COLLECTION, groupId));
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const group = groupDoc.data();
    if (group.createdBy !== userId) {
      throw new Error('Only the owner can delete the group');
    }

    await deleteDoc(doc(db, this.GROUPS_COLLECTION, groupId));
  }

  /**
   * Update group name (owner only)
   */
  async updateGroupName(groupId: string, newName: string, userId: string): Promise<void> {
    const groupDoc = await getDoc(doc(db, this.GROUPS_COLLECTION, groupId));
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const group = groupDoc.data();
    if (group.createdBy !== userId) {
      throw new Error('Only the owner can update the group name');
    }

    await updateDoc(doc(db, this.GROUPS_COLLECTION, groupId), {
      name: newName,
    });
  }
}

export const groupService = new GroupService();

